import {Injectable} from '@angular/core';
import {arrayUpdate} from '@datorama/akita';
import {Team} from './team';
import {Observable, of, OperatorFunction, Subject, throwError} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {TeamsRepresentation} from './teams-representation';
import {environment} from '../../../environments/environment';
import {catchError, map, tap} from 'rxjs/operators';
import {NewTeamRepresentation} from './new-team-representation';
import {TeamStore} from './team.store';
import {TeamRepresentation} from './team-representation';
import {TeamQuery} from './team.query';
import {TeamUser} from './team-user';
import {TeamsBasicInfoRepresentation} from './teams-basic-representation';

const API_URL = environment.API_URL;

@Injectable({
  providedIn: 'root'
})
export class TeamService {

  bubblesHidden = new Subject<boolean>();
  motivesHidden = new Subject<void>();
  motivesVariationChanged = new Subject<number>();
  resetView = new Subject<void>();

  constructor(private http: HttpClient,
              private teamQuery: TeamQuery,
              private teamStore: TeamStore) {
  }

  storeTeam(team: Team): void {
    const updatedTeam: Team = {
      teamId: team.teamId,
      creatorId: team.creatorId,
      name: team.name,
      users: team.users
    };
    this.teamStore.upsert(team.teamId, () => updatedTeam);
  }

  storeTeams(teams: Team[]): void {
    teams.forEach(team => this.storeTeam(team));
  }

  loadTeams(): Observable<Team[]> {
    this.teamStore.setLoading(true);
    return this.http.get<TeamsRepresentation>(
      API_URL + '/team',
      {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      }
    ).pipe(
      map((teamsBasicInfo: TeamsBasicInfoRepresentation) =>
        // Konvertiere BasicInfo in Team-Objekte mit leeren users-Arrays
        teamsBasicInfo.teams.map(basicInfo => ({
          teamId: basicInfo.teamId,
          creatorId: basicInfo.creatorId,
          name: basicInfo.teamName,
          users: undefined
        }))
      ),
      tap((teams: Team[]) => {
        this.storeTeams(teams);
        if (teams.length !== 0) {
          teams.sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            return 0;
          });

          // Wähle erstes Team aus und lade dessen Details
          this.changedSelectedTeam(teams[0]);
          this.loadTeamDetails(teams[0].teamId).subscribe();
        }
        this.teamStore.setLoading(false);
      }, (error) => {
        console.log(error);
        this.teamStore.setLoading(false);
      })
    );
  }

  loadTeamDetails(teamId: string): Observable<Team> {
    // Prüfe, ob Team bereits Details hat
    const existingTeam = this.teamQuery.getEntity(teamId);

    // If we have already loaded the team (users is defined, even if empty), don't reload
    if (existingTeam?.users !== undefined) {
      return of(existingTeam);
    }

    return this.http.get<TeamRepresentation>(
      API_URL + '/team/' + teamId + '/details',
      {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      }
    ).pipe(
      map((teamRepresentation: TeamRepresentation) =>
        TeamRepresentation.toModelEntity(teamRepresentation)),
      tap((team: Team) => {
        // Update des Teams mit vollständigen Details
        this.storeTeam(team);

        // Wenn es das aktuell ausgewählte Team ist, aktualisiere die Auswahl
        if (this.teamQuery.getValue().selectedTeam?.teamId === teamId) {
          this.changedSelectedTeam(team);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return throwError(error);
      })
    );
  }

  changedSelectedTeam(selectedTeam: Team): void {
    this.teamStore.update({selectedTeam});
    // Only load details if team exists and users array is undefined
    if (selectedTeam && !selectedTeam.users) {
      this.loadTeamDetails(selectedTeam.teamId).subscribe();
    }
    else {
      this.checkFadedMembers();
    }
  }

  changedSelectedTeamView(teamUser: TeamUser, hide: boolean): void {
    this.teamStore.update(this.teamStore.getValue().selectedTeam.teamId, (state) => ({
      users: arrayUpdate(state.users, user => user.keycloakId === teamUser.keycloakId, {
        keycloakId: teamUser.keycloakId, hide
      })
    }));
    this.changedSelectedTeam(this.teamQuery.getEntity(this.teamStore.getValue().selectedTeam.teamId));
  }

  resetTeamView(): void {
    this.teamStore.update(this.teamStore.getValue().selectedTeam.teamId, (state) => ({
      users: arrayUpdate(state.users, users => users.hide === true, {hide: false})
    }));
    this.changedSelectedTeam(this.teamQuery.getEntity(this.teamStore.getValue().selectedTeam.teamId));
    this.resetView.next();
  }

  createTeam(newTeamRepresentation: NewTeamRepresentation): Observable<Team> {
    return this.http.post<TeamRepresentation>(
      API_URL + '/team',
      newTeamRepresentation,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json;version=1'
        })
      }
    ).pipe(
      map((teamRepresentation: TeamRepresentation) =>
        TeamRepresentation.toModelEntity(teamRepresentation)),
      tap((team: Team) => {
        this.storeTeam(team);
        this.changedSelectedTeam(team);
      })
    );
  }

  updateTeam(teamId: string, newTeam: NewTeamRepresentation): Observable<Team> {
    return this.http.patch<TeamRepresentation>(
      API_URL + '/team/' + teamId,
      newTeam,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json;version=1'
        })
      }
    ).pipe(
      map((teamRepresentation: TeamRepresentation) =>
        TeamRepresentation.toModelEntity(teamRepresentation)),
      tap((team: Team) => {
        this.storeTeam(team);
        this.changedSelectedTeam(team);
      }),
      catchError(
        (error: HttpErrorResponse): Observable<Team> => {
          console.error(error);
          return throwError(error);
        }
      )
    );
  }

  deleteTeam(team: Team): Observable<any> {
    this.teamStore.remove(team.teamId);
    return this.http.delete<void>(
      API_URL + '/team/' + team.teamId,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json;version=1'
        })
      }
    ).pipe(
      catchError(
        (error: HttpErrorResponse): Observable<void> => {
          // Restore team on error.
          this.storeTeam(team);
          // Rethrow.
          return throwError(error);
        }
      ) as OperatorFunction<void, any>
    );
  }

  private checkFadedMembers(): void {
    const selectedTeam = this.teamQuery.getValue().selectedTeam;
    if (selectedTeam && selectedTeam.users) {
      if (selectedTeam.users.some(user => user.hide === true)) {
        this.bubblesHidden.next(true);
      }
      else {
        this.bubblesHidden.next(false);
      }
    }
  }
}
